import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import ReduxActions from '../store/ReduxActions';
import { actionCreators } from '../store/actions';
import useBasket from '../hooks/basket/useBasket';
import { ErrorHandlerInterface, IMap, Order, OrderData } from '../services/exports/Interfaces';
import OrderManager from '../services/api/OrderManager';
import {
  COUPON_TYPES,
  CUSTOMER_INPUT_KEYS,
  DeliveryProvider,
  DropoffAction,
  ERROR_CODES,
  ORDER_METHODS,
  PaymentMethod,
  PaypalVariant,
  RoomServiceLocationType,
} from '../services/exports/Constants';
import { DebouncedFunc, debounce } from 'lodash';
import usePaymentFlow from '../hooks/payment/usePaymentFlow';
import collect from 'collect.js';
import moment from 'moment';
import DuplicateOrderModal from '../modals/checkout/DuplicateOrderModal';
import { PaymentIntent } from '@stripe/stripe-js';
import useAlert from '../hooks/utility/useAlert';
import {
  DataWrappedResponse,
  ErrorResponse,
  ReservedHttpResponseCodes,
  ValidationErrorResponse,
  ValidationErrorResponseData,
} from '../services/exports/Types';
import useAuth from '../hooks/global/useAuth';
import { useTranslation } from 'react-i18next';
import useSessionStorage, {
  SessionStorageReservedKey,
  composeSessionStorageKey,
  removeSessionStorageKeyForAllCompanies,
} from '../hooks/global/useSessionStorage';
import useDeepRef from '../hooks/utility/useDeepRef';
import { getUtm } from '../services/exports/Cookies';
import useInitialData from '../hooks/global/useInitialData';
import useOffersStuff from '../features/offers/hooks/useOffersStuff';
import useGiftCard from '../features/gift-cards/hooks/useGiftCard';
import mixpanel from 'mixpanel-browser';
import { resolveMixpanelProject } from '../services/utilities/mixpanelProject';
import { mayShareMixpanelIdentity } from '../utils/analytics/platformConsent';
import { hasTrackingConsent as hasTrackingConsentFor } from '../utils/cookieConsent';
import useMobileApp from '../hooks/mobile-app/useMobileApp';
import toast from 'react-hot-toast';
import AddressHelpers from '../services/helpers/AddressHelpers';
import paymentLog, { Attrs, PaymentGatewayKey } from '../services/helpers/PaymentLogger';
import useReconcileCashbackPoints from '../hooks/cashback/useReconcileCashbackPoints';
import discountDisplacementSlice from '../store/slices/DiscountDisplacementSlice';
import { isFreeOrderDraft, resolveCheckoutPaymentMethod } from '../services/helpers/checkoutPaymentMethod';

interface Props {
  children: ReactElement;
}

interface UpdateDataFunction {
  (field: string, value: any): void;
  (data: Partial<OrderData>): void;
}

/**
 * Why the last draft attempt failed.
 *
 * `delivery_details` — the address is incomplete or the backend rejected a `delivery_*`
 * field. This one is recoverable in place where the order-method modal is reachable, and
 * needs its own copy where it is not, so it is kept apart from the generic case.
 * `customer_input` — the backend refused a field the checkout form shows an inline error for.
 * Normal typing, not a defect: it must not replace the form with a full-screen error, because
 * that hides the very input the customer has to correct, and "Try again" would resend it.
 * `rejected` — anything else: a gateway field, a network failure, a 5xx. A real fault.
 *
 * Which screens act on which reason is decided in one place, `useDraftError` — not here and
 * not at the four call sites, where two copies of the rule had already drifted apart.
 */
export type DraftFailureReason = 'delivery_details' | 'customer_input' | 'rejected';

interface DraftStatus {
  /** A redraft is waiting out the debounce. */
  scheduled: boolean;
  /** The latest draft request has not answered yet. */
  inFlight: boolean;
  /** The latest draft that answered succeeded. */
  succeeded: boolean;
}

interface OrderDraftContextInterface {
  data: Order;
  localData: Omit<OrderData, 'line_items'>;
  ongoingOrder: Order;
  loading: boolean;
  isOrderMethodInfoComplete: boolean;
  setOrderMethodInfoInfoComplete: () => void;
  isExternalDelivery: boolean;
  isCustomerInfoComplete: boolean;
  /**
   * True once a draft attempt has failed and no draft has succeeded since. Screens that
   * block on a draft read this so a failure renders an error instead of a loader that
   * never clears.
   */
  draftFailed: boolean;
  draftFailureReason: DraftFailureReason | null;
  /**
   * True from the moment a redraft is scheduled (the 250ms debounce) until its response lands.
   * `loading` only covers the request itself, so it is false during the debounce while `data`
   * still describes the order before the change.
   */
  isDraftSettling: boolean;
  /**
   * `data` describes the order as it is now: nothing is settling and the latest draft succeeded. A
   * failed draft keeps the previous `data`, so its total must not be trusted on its own.
   */
  isDraftCurrent: boolean;
  retryDraft: () => void;
  validationErrors: null | ValidationErrorResponseData;
  getOrderData: (data?: Partial<OrderData | { ignored_error_codes: string[] }>) => OrderData;
  draftOrder: (data?: Partial<OrderData>) => Promise<DataWrappedResponse<Order, 200> | ValidationErrorResponse>;
  setDraft: (data: Order) => void;
  setDraftUuid: (uuid: string) => void;
  /**
   * The order a failed Payment Element attempt left pending, so the next attempt retries it
   * (PUT) instead of creating another one. Cleared on an order-method change or a logout; null
   * while a shared draft is open, or when the stored order was for another method or customer.
   */
  getRetryOrderUuid: () => string | null;
  /** `companyId` clears another company's slot, e.g. the one a shared draft link belongs to. */
  setRetryOrderUuid: (uuid: string | null, companyId?: number) => void;
  createOrder: (
    paymentIntent: null | PaymentIntent,
    extraData?: Partial<OrderData>,
    errorHandlers?: IMap<() => void, string>,
    headers?: IMap<string>,
    retryOrderUuid?: string | null,
  ) => Promise<
    | DataWrappedResponse<Order, 201>
    | ErrorResponse<
        { order: Order; payment_intent: PaymentIntent },
        ReservedHttpResponseCodes.PaymentConfirmationRequired
      >
    | ErrorResponse<{ error_code: string }, 400>
    | ErrorResponse<{ error_code: string }, 403>
    | ErrorResponse<any, 404>
    | ErrorResponse<any, ReservedHttpResponseCodes.ErrorResponseModal>
    | ErrorResponse<any, ReservedHttpResponseCodes.ErrorResponseAlertWindow>
    | ErrorResponse<any, ReservedHttpResponseCodes.TooManyRequests>
    | ValidationErrorResponse
  >;
  checkOrderNotDuplicate: () => Promise<boolean>;
  refreshOngoingOrder: () => Promise<null | Order>;
  getOrderMethodName: (order: Partial<Order>) => string;
  addOngoingOrder: (data: Order) => void;
  removeOngoingOrder: (id: number) => void;
  setValidationErrors: (value: null | ValidationErrorResponseData) => void;
  resetValidationErrors: () => void;
  update: UpdateDataFunction;
  resetOrder: (data?: Partial<OrderData>) => void;
  setIgnorePaymentMethod: (value: boolean) => void;
}

export const OrderFlowContext = React.createContext<OrderDraftContextInterface>(null);

export enum ORDER_METHOD_INFO_COMPLETION_STATUS {
  complete = 'complete',
}

export default function OrderFlowProvider(props: Props): ReactElement {
  const { t } = useTranslation(undefined, { keyPrefix: 'Providers:OrderFlowProvider' });

  const { children } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order, ongoing_orders } = useSelector((state: StoreInterface) => state.order);
  const { profile } = useSelector((state: StoreInterface) => state.profile);
  const { cookiesConsent } = useSelector((state: StoreInterface) => state.appStatus);
  const { isMobileApp } = useMobileApp();

  const alert = useAlert();
  const { isGuest } = useAuth();
  const { getItem, setItem, removeItem } = useSessionStorage();
  const { selectedPaymentMethod, paypalVariant, shouldSavePaymentInfo, onlinePaymentsGateway } = usePaymentFlow();
  const { items, formatItemForBackend } = useBasket();

  // Sentry fingerprints payment events on ['payment', gateway, event]. Keying draft
  // failures to the company's online rail keeps a Spreedly incident from sharing an
  // alert with every other gateway's.
  const logGateway: PaymentGatewayKey = onlinePaymentsGateway ?? 'unknown';
  const itemsRef = useDeepRef(items);
  const { forceRefresh } = useInitialData();
  const { remove, giftCard } = useGiftCard();
  useReconcileCashbackPoints();

  const hasTips = company?.has_tips;
  const [isOrderMethodInfoComplete, setIsOrderMethodInfoInfoComplete] = useState(
    () => getItem(SessionStorageReservedKey.OrderMethodInfoStatus) === ORDER_METHOD_INFO_COMPLETION_STATUS.complete,
  );
  const setOrderMethodInfoInfoComplete = (value: boolean = true) => {
    setItem(
      SessionStorageReservedKey.OrderMethodInfoStatus,
      value ? ORDER_METHOD_INFO_COMPLETION_STATUS.complete : null,
    );
    setIsOrderMethodInfoInfoComplete(value);
  };

  const [draftUuid, setDraftUuid] = useState<string>(null);
  const draftUuidRef = useDeepRef<string>(draftUuid);
  const [draft, setDraft] = useState<Order>(cached_order as Order);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorResponseData>(null);
  const [draftFailureReason, setDraftFailureReason] = useState<DraftFailureReason | null>(null);
  const draftFailed = draftFailureReason !== null;
  const [ignorePaymentMethod, setIgnorePaymentMethod] = useState<boolean>(false);

  // `draftRef` and `draftStatusRef` are written synchronously next to the state they mirror, so an
  // order created from a callback that closed over an older render still sees the latest draft.
  const draftRef = useRef<Order>(draft);
  const draftSequence = useRef(0);
  const draftStatusRef = useRef<DraftStatus>({ scheduled: false, inFlight: false, succeeded: false });
  const [draftStatus, setDraftStatus] = useState<DraftStatus>(draftStatusRef.current);
  const isDraftSettling = draftStatus.scheduled || draftStatus.inFlight;
  const isDraftCurrent = !isDraftSettling && !draftFailed && draftStatus.succeeded;

  const updateDraftStatus = useCallback((patch: Partial<DraftStatus>) => {
    const current = draftStatusRef.current;
    const next = { ...current, ...patch };

    if (
      next.scheduled === current.scheduled &&
      next.inFlight === current.inFlight &&
      next.succeeded === current.succeeded
    ) {
      return;
    }

    draftStatusRef.current = next;
    setDraftStatus(next);
  }, []);

  const applyDraft = useCallback((order: Order) => {
    draftRef.current = order;
    setDraft(order);
  }, []);

  // A draft handed in from outside (OrderDraftPreloader's recovered pending order) comes from the
  // server and is current as it stands. The preloader also writes the order's uuid into
  // `cached_order`, which stops the draft effect, so nothing else would ever mark it current, and
  // a recovered free order could then not be placed.
  const adoptDraft = useCallback(
    (order: Order) => {
      draftSequence.current += 1;
      applyDraft(order);
      setDraftFailureReason(null);
      updateDraftStatus({ scheduled: false, inFlight: false, succeeded: true });
    },
    [applyDraft, updateDraftStatus],
  );

  const isDraftCurrentNow = (): boolean => {
    const { scheduled, inFlight, succeeded } = draftStatusRef.current;

    return !scheduled && !inFlight && succeeded;
  };

  // Leading-edge debounce: surface the first delivery-validation failure immediately,
  // then suppress duplicates while the user is still editing the address. Without this,
  // every keystroke that produces a failed draft fires a toast + Sentry warning.
  const reportDeliveryError = useMemo(
    () =>
      debounce(
        (errors: ValidationErrorResponseData['errors'], method: string, message: string) => {
          toast.error(message);
          console.warn('Order draft validation failed (422)', { errors, method });
        },
        3000,
        { leading: true, trailing: false },
      ),
    [],
  );

  useEffect(() => () => reportDeliveryError.cancel(), [reportDeliveryError]);

  // Same leading-edge shape for the draft rejections that go to Sentry. The draft effect
  // refires on every basket, method or address change, and each refire would otherwise
  // emit its own event for what is one ongoing failure. Both levels are Sentry Logs counted
  // in `payments.events`; `error` marks a draft the customer cannot fix by editing the form.
  //
  // One debounce PER EVENT, not one shared by all of them. A single shared window would
  // let the routine `warn` for an address the customer has not typed yet — which fires on
  // essentially every delivery draft before checkout — swallow the `error` that follows it
  // within 3s, and that error is the entire reason this instrumentation exists.
  const draftRejectionDebounces = useRef(new Map<string, DebouncedFunc<typeof paymentLog.warn>>());

  const reportDraftRejection = useCallback(
    (level: 'warn' | 'error', gateway: PaymentGatewayKey, event: string, attrs: Attrs) => {
      const key = `${level}:${event}`;
      const existing = draftRejectionDebounces.current.get(key);

      const report =
        existing ??
        debounce((...args: Parameters<typeof paymentLog.warn>) => paymentLog[level](...args), 3000, {
          leading: true,
          trailing: false,
        });

      if (!existing) {
        draftRejectionDebounces.current.set(key, report);
      }

      report(gateway, event, attrs);
    },
    [],
  );

  useEffect(() => {
    const debounces = draftRejectionDebounces.current;

    return () => debounces.forEach((report) => report.cancel());
  }, []);
  const [loading, setLoading] = useState(false);
  const duplicateOrderAwareRef = useRef<boolean>(false);
  const [duplicateOrderModalProps, setDuplicateOrderModalProps] = useState({
    open: false,
    order: null,
  });
  const { appliedOffers } = useOffersStuff();

  // GDPR: attribution and the consent flag the backend gates Meta CAPI / Mixpanel order_placed on.
  // Without consent no utm is sent, even if a stale cookie is still around.
  const hasTrackingConsent = hasTrackingConsentFor(cookiesConsent, isMobileApp);
  const utm = (hasTrackingConsent && getUtm()) || {};

  const isExternalDelivery = useMemo(
    () =>
      cached_order.method === ORDER_METHODS.DELIVERY && cached_order.delivery_provider !== DeliveryProvider.Standalone,
    [cached_order.method, cached_order.delivery_provider],
  );

  const isCustomerInfoComplete = useMemo(
    () =>
      Boolean(cached_order.customer_phone_number) &&
      Boolean(cached_order.customer_email) &&
      Boolean(cached_order.customer_first_name) &&
      Boolean(cached_order.customer_last_name),
    [
      cached_order.customer_phone_number,
      cached_order.customer_email,
      cached_order.customer_first_name,
      cached_order.customer_last_name,
    ],
  );

  const ongoingOrder = useMemo(
    () => collect(ongoing_orders).firstWhere('company_id', company?.id),
    [ongoing_orders, company?.id],
  );

  // The slot lives in sessionStorage (keyed per company by useSessionStorage) so it survives the
  // reload a bank redirect causes, and dies with the tab. Read through refs: `checkout` reads it
  // again from callbacks that closed over an older render.
  const retrySlotRef = useDeepRef({
    getItem,
    setItem,
    removeItem,
    method: cached_order.method,
    owner: profile?.id ?? null,
  });

  const setRetryOrderUuid = useCallback((uuid: string | null, companyId?: number) => {
    const { setItem, removeItem, method, owner } = retrySlotRef.current;

    // A shared draft's order may carry attempts of any method, whose open intents a Payment Element
    // retry would not cancel: it is never kept for one.
    if (uuid && draftUuidRef.current) {
      return;
    }

    try {
      if (uuid) {
        setItem(SessionStorageReservedKey.PaymentElementRetryOrder, JSON.stringify({ uuid, method, owner }));
      } else if (companyId) {
        sessionStorage.removeItem(
          composeSessionStorageKey(companyId, SessionStorageReservedKey.PaymentElementRetryOrder),
        );
      } else {
        removeItem(SessionStorageReservedKey.PaymentElementRetryOrder);
      }
    } catch {
      // Storage refused (private mode, quota): the next attempt is a new order, as before.
    }
  }, []);

  const getRetryOrderUuid = useCallback((): string | null => {
    if (draftUuidRef.current) {
      return null;
    }

    const { getItem, method, owner } = retrySlotRef.current;

    try {
      const stored = JSON.parse(getItem(SessionStorageReservedKey.PaymentElementRetryOrder) ?? 'null');

      // A method change or a logout clears the slot; this is the backstop for anything that slipped
      // past them (e.g. a guest signing in mid-checkout): another method's or customer's order is not
      // this checkout's to retry.
      if (!stored?.uuid || stored.method !== method || (stored.owner ?? null) !== owner) {
        return null;
      }

      return stored.uuid;
    } catch {
      return null;
    }
  }, []);

  // An emptied basket ends the order the slot names. Read from the persisted line items: the
  // compiled `items` are empty until the menu has loaded, which on a redirect's reload would drop it.
  const rawLineItemCount = useSelector((state: StoreInterface) => state.basket?.lineItems?.[company?.id]?.length ?? 0);

  useEffect(() => {
    if (company?.id && rawLineItemCount === 0) {
      setRetryOrderUuid(null);
    }
  }, [company?.id, rawLineItemCount]);

  // A change of order method ends the order every slot names: the method is shared by a franchise's
  // companies, the slots are not. The ref starts at the method this mount was rendered with (already
  // rehydrated by PersistGate), so a remount or a redirect's reload clears nothing. Method-only on purpose.
  const lastOrderMethodRef = useRef(cached_order.method);

  useEffect(() => {
    const previous = lastOrderMethodRef.current;
    lastOrderMethodRef.current = cached_order.method;

    if (previous && cached_order.method && previous !== cached_order.method) {
      removeSessionStorageKeyForAllCompanies(SessionStorageReservedKey.PaymentElementRetryOrder);
    }
  }, [cached_order.method]);

  const debouncedDraft = debounce(async (data?: OrderData) => {
    updateDraftStatus({ scheduled: false });

    return draftOrder(data);
  }, 250);

  // A placed order is written into `cached_order` (it gains a uuid) while the basket is still full —
  // the basket and the order are only reset once the order screen or tracker takes over. That write
  // changes the dependencies below, and the redraft it triggered was re-validated against state the
  // order had just consumed — the cashback points it debited came back as a `cashback_points` 422.
  const isOrderPlaced = Boolean(cached_order.uuid);

  useEffect(() => {
    const shouldDraft = Boolean(company?.id && items?.length && !isOrderPlaced);

    updateDraftStatus({ scheduled: shouldDraft });
    shouldDraft && debouncedDraft();

    return () => debouncedDraft.cancel();
  }, [
    isOrderPlaced,
    company?.id,
    items,
    ignorePaymentMethod,
    selectedPaymentMethod,
    paypalVariant,
    cached_order.tip,
    cached_order.method,
    cached_order.coupon_type,
    cached_order.coupon_details,
    cached_order.cashback_points,
    cached_order.delivery_lat,
    cached_order.delivery_lng,
    cached_order.delivery_quote?.id,
    appliedOffers,
    giftCard?.code,
  ]);

  useEffect(() => {
    if (isReduxOrderValid()) {
      return;
    }

    return resetOrder();
  }, []);

  useEffect(() => {
    addMissingOrderFields();
  }, []);

  useEffect(() => {
    refreshOngoingOrder();
  }, []);

  // A draft with no method chosen yet stays unpriced-by-method (null) instead of falling back
  // to cash: the backend zeroes the tip and the service fee for cash, which is what made the
  // payment page under-report the total before a method was picked. Order creation still needs
  // a concrete method; `createOrder` resolves it through resolveCheckoutPaymentMethod.
  const resolvePaymentMethodId = useCallback(
    (fallback: PaymentMethod | null): PaymentMethod | null => {
      if (ignorePaymentMethod) {
        return null;
      }

      if (selectedPaymentMethod === PaymentMethod.Paypal && paypalVariant === PaypalVariant.Direct) {
        return PaymentMethod.PaypalStandalone;
      }

      return selectedPaymentMethod ?? fallback;
    },
    [ignorePaymentMethod, selectedPaymentMethod, paypalVariant],
  );

  const getOrderData = useCallback(
    (data?: Partial<OrderData>): OrderData => ({
      ...cached_order,
      method: cached_order.method,
      company_id: company?.id,
      payment_method_id: resolvePaymentMethodId(null),
      should_save_payment_info: shouldSavePaymentInfo,
      delivery_quote_id: isExternalDelivery ? cached_order.delivery_quote?.id : null,
      delivery_dropoff_action: isExternalDelivery
        ? (cached_order.delivery_dropoff_action ?? DropoffAction.RingDoorBell)
        : null,
      promo: cached_order.coupon_details?.code,
      cashback_points:
        cached_order.coupon_type === COUPON_TYPES.CASHBACK ? (cached_order.cashback_points ?? null) : null,
      tip: !hasTips ? 0 : (cached_order.tip ?? 0),
      line_items: itemsRef.current.map((item) => formatItemForBackend(item)),
      applied_offers: appliedOffers?.map((appliedOffer) => ({ id: appliedOffer.id })),
      gift_card_code: giftCard?.id,
      utm,
      // The backend sends the order to Meta's Conversions API and Mixpanel itself, so it needs the
      // consent too. The app always counts as consent (see hasTrackingConsent). On the web only an
      // explicit accept counts, and the field is left out while the banner has not been answered, so
      // the backend records "never asked" rather than a decline the customer did not make.
      ...(hasTrackingConsent || cookiesConsent !== undefined ? { tracking_consent: hasTrackingConsent } : {}),
      ...data,
    }),
    [
      appliedOffers,
      cached_order,
      company?.id,
      cookiesConsent,
      hasTips,
      isExternalDelivery,
      resolvePaymentMethodId,
      shouldSavePaymentInfo,
      itemsRef.current,
      utm,
      hasTrackingConsent,
    ],
  );

  const handleOrderDraftResult = (order: Order) => {
    applyDraft(order);

    if (
      cached_order.method === ORDER_METHODS.DELIVERY &&
      (!cached_order.delivery_lat || !cached_order.delivery_lng || !cached_order.delivery_zip_code)
    ) {
      const replace = collect({
        delivery_lat: order.delivery_lat,
        delivery_lng: order.delivery_lng,
        delivery_zip_code: order.delivery_zip_code,
      }).filter((value, key) => !cached_order[key]).items;

      ReduxActions.dispatch(actionCreators.order.updateOrder(replace));
    }

    if (giftCard?.code && order.coupon_type !== COUPON_TYPES.GIFT_CARD) {
      remove();
    }

    if (!appliedOffers?.length && !order.applied_offers?.length) {
      return;
    }

    const hasOffersMismatch =
      appliedOffers?.length !== order.applied_offers?.length ||
      appliedOffers?.some(
        (appliedOffer) => !order.applied_offers?.some((orderOffer) => orderOffer.id === appliedOffer.id),
      );

    hasOffersMismatch && forceRefresh();
  };

  async function draftOrder(data?: OrderData) {
    const merged = getOrderData(data);
    const sequence = ++draftSequence.current;

    if (merged.method === ORDER_METHODS.DELIVERY && !AddressHelpers.isDeliveryComplete(merged)) {
      // Supersedes any request still in flight, whose response will now be ignored, so the loading
      // flag that request raised has to be dropped here.
      setLoading(false);
      updateDraftStatus({ inFlight: false, succeeded: false });
      setOrderMethodInfoInfoComplete(false);

      // No request is made, but the screens that block on a draft still need to know one
      // is not coming — otherwise this path keeps the loader up forever, the same dead
      // end the network failures below used to produce. A customer who has not typed an
      // address yet is a normal state, not a payment error: breadcrumb only.
      setDraftFailureReason('delivery_details');
      reportDraftRejection('warn', logGateway, 'order_draft_incomplete_delivery_address', { method: merged.method });

      return {
        success: false,
        response: null,
        status: ReservedHttpResponseCodes.ValidationError,
      } as ValidationErrorResponse;
    }

    setLoading(true);
    updateDraftStatus({ inFlight: true });
    const request = await OrderManager.draftOrder(merged);

    // A later draft has started since: this response describes an order that no longer exists, and
    // applying it after the newer one would leave the screen on the older total.
    if (sequence !== draftSequence.current) {
      return request;
    }

    setLoading(false);
    updateDraftStatus({ inFlight: false, succeeded: Boolean(request.success) });

    if (request.success) {
      setDraftFailureReason(null);
      handleOrderDraftResult(request.response.data.data);

      return request;
    }

    // Any unsuccessful draft is terminal for the screens that block on one. Previously
    // only `delivery_*` validation keys were surfaced, so every other failure — a
    // gateway field, a network blip, a closed restaurant — left the payment page behind
    // a loader with no way out.
    if (request.status === ReservedHttpResponseCodes.ValidationError) {
      setDraftFailureReason(handleDraftValidationError(request as ValidationErrorResponse));

      return request;
    }

    setDraftFailureReason('rejected');

    // Debounced like every other draft rejection, and for a stronger reason: this is the
    // branch a flaky connection takes, and `retryDraft` is a button the customer can hold
    // down. Reporting it undebounced would log one error per click.
    //
    // No status means no HTTP response at all — a phone that lost signal, or a request aborted
    // by navigating away. That is the network's business, not a defect of ours, so it stays a
    // breadcrumb; `error` is kept for a status the server actually chose.
    reportDraftRejection(request.status ? 'error' : 'warn', logGateway, 'order_draft_failed', {
      status: request.status ?? 'none',
      method: merged.method,
    });

    return request;
  }

  function retryDraft(): void {
    setDraftFailureReason(null);
    draftOrder();
  }

  function handleDraftValidationError(request: ValidationErrorResponse): DraftFailureReason {
    const errors = request.response?.response?.data ?? null;
    setValidationErrors(errors);

    const errorKeys = errors?.errors ? Object.keys(errors.errors) : [];
    const hasDeliveryErrors = errorKeys.some((key) => key.startsWith('delivery_'));

    if (hasDeliveryErrors) {
      setOrderMethodInfoInfoComplete(false);

      const isOnlyDeliveryQuoteId = errorKeys.length === 1 && errorKeys[0] === 'delivery_quote_id';

      if (!isOnlyDeliveryQuoteId) {
        reportDeliveryError(errors?.errors, cached_order?.method, t('errors.draft_validation_failed'));
      }

      return 'delivery_details';
    }

    // Every rejected key is one the form itself renders under its input, so the customer is
    // already being told. Breadcrumb only: reporting these as errors made a Sentry issue out of
    // ordinary typing — 67 users inside four hours, almost all of them a half-entered email.
    if (errorKeys.length > 0 && errorKeys.every((key) => CUSTOMER_INPUT_KEYS.includes(key))) {
      reportDraftRejection('warn', logGateway, 'order_draft_customer_input_rejected', {
        keys: errorKeys.join(','),
        method: cached_order?.method,
      });

      return 'customer_input';
    }

    // A draft rejected on anything but the address or the customer's own input is a bug in the
    // request we sent, not something the customer can correct — the Spreedly card-token 422 was
    // exactly this. Report it so it is visible rather than silently trapping the customer.
    reportDraftRejection('error', logGateway, 'order_draft_validation_failed', {
      keys: errorKeys.join(','),
      method: cached_order?.method,
    });

    return 'rejected';
  }

  async function createOrder(
    paymentIntent: null | PaymentIntent,
    extraData: Partial<OrderData> = {},
    errorHandlers: IMap<() => void, string> = {},
    headers: IMap<string> = {},
    retryOrderUuid: string | null = null,
  ) {
    // `POST /orders` requires a concrete method. Cash stands in only for a free order whose draft is
    // current; anything else with no method is the customer's to choose, not ours to charge as cash.
    const { method, source } = resolveCheckoutPaymentMethod({
      explicit: extraData.payment_method_id,
      explicitSource: extraData.payment_method_source,
      selected: resolvePaymentMethodId(null),
      isFreeOrder: isFreeOrderDraft(draftRef.current?.total, isDraftCurrentNow()),
    });

    if (!method) {
      paymentLog.issue(logGateway, 'order_create_blocked_no_payment_method', {
        draft_total: draftRef.current?.total ?? 'none',
        is_draft_current: isDraftCurrentNow(),
        has_adyen_payment_data: Boolean(extraData.adyen_payment_data),
        method: cached_order?.method,
      });

      // The shape of a backend 422 on `payment_method_id`, so the checkout's existing validation
      // routing takes the customer to the payment section.
      return {
        success: false,
        response: {
          response: {
            data: { message: null, errors: {}, rules: { payment_method_id: { required: [] } } },
          },
        },
        status: ReservedHttpResponseCodes.ValidationError,
      } as unknown as ValidationErrorResponse;
    }

    const params: [OrderData, ErrorHandlerInterface, IMap<string>] = [
      getOrderData({
        ...extraData,
        payment_method_id: method,
        payment_method_source: source,
        payment_intent_id: paymentIntent?.id,
        // Ties the backend's order_placed to this browser's Mixpanel identity, on whichever project
        // AnalyticsProvider initialised (the provider's own or the platform fallback). Only with consent:
        // without it Mixpanel was never initialised, and the backend drops the id anyway.
        ...(hasTrackingConsent &&
        resolveMixpanelProject(company?.service_provider) &&
        mixpanel?.['__loaded'] &&
        mayShareMixpanelIdentity(mixpanel)
          ? {
              customer_distinct_id: mixpanel.get_distinct_id(),
            }
          : {}),
      }),
      {
        [ERROR_CODES.CARD_EXCEPTION]: () => null,
        [ERROR_CODES.ORDER_FULFILLMENT_FAILED]: () => null,
        [ERROR_CODES.DELIVERY_THRESHOLD_EXCEEDED]: () => null,
        [ERROR_CODES.PICKUP_CLOSED]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.DELIVERY_CLOSED]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.DELIVERY_UNAVAILABLE]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.DELIVERY_QUOTE_ALREADY_USED]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.ORDERS_PAUSED]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.ORDER_TIME_NOT_AVAILABLE]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.ORDER_SLOT_IS_TAKEN]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.ROOM_SERVICE_LOCATION_UNAVAILABLE]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.ROOM_SERVICE_UNAVAILABLE]: () => setOrderMethodInfoInfoComplete(false),
        ...errorHandlers,
      },
      headers,
    ];

    // A shared draft wins over a Payment Element retry: both are an order already persisted.
    const putUuid = draftUuid ?? retryOrderUuid;
    const response = putUuid
      ? await OrderManager.updateOrder(putUuid, ...params)
      : await OrderManager.createOrder(...params);

    if (!response.success) {
      orderErrorHandler(response);

      return response;
    }

    return response;
  }

  function orderErrorHandler(error: any): void {
    if (error?.response?.status === ReservedHttpResponseCodes.ErrorResponseAlertWindow) {
      alert.error({
        title: error?.response?.data?.title,
        description: error?.response?.data?.description,
      });
    }
  }

  async function checkOrderNotDuplicate(): Promise<boolean> {
    if (duplicateOrderAwareRef.current || isGuest) {
      return true;
    }

    const { success, response } = await OrderManager.getOrderHistory({
      filter: { company_id: company?.id },
      limit: 1,
    });

    if (!success) {
      return true;
    }

    const order = collect(response.data.data).first();

    if (order && moment().diff(moment(order.paid_at), 'minutes') < 15) {
      toggleDuplicateOrderModal(order);

      return false;
    }

    return true;
  }

  async function refreshOngoingOrder(): Promise<null | Order> {
    if (!ongoingOrder) {
      return;
    }

    const { success, response } = await OrderManager.getOrder(ongoingOrder.uuid);

    const order: Order | null = success ? (response?.data?.data ?? null) : null;

    // A response that ended without a body (SHOP-WEB-46) says nothing about the order: kept as it is.
    if (!order) {
      return;
    }

    if (moment(order.fulfillment.estimated_pickup_time).isBefore(moment().subtract(15, 'minutes'))) {
      ReduxActions.dispatch(actionCreators.order.removeOngoingOrder(ongoingOrder.id));

      return null;
    }

    ReduxActions.dispatch(actionCreators.order.addOngoingOrder(order));

    return order;
  }

  function resetOrder(data: Partial<OrderData> = {}) {
    const payload = {
      ...data,
      ...(profile
        ? {
            customer_phone_number: profile.phone_number,
            customer_email: profile.email,
            customer_first_name: profile.first_name,
            customer_last_name: profile.last_name,
            marketing_consent: profile.is_marketing_subscriber,
          }
        : {}),
    };

    setDraftUuid(null);
    setRetryOrderUuid(null);
    // The displacement records name the coupon/points of the order being reset; left behind, a
    // later session's notice would offer to Undo a swap from an order already placed.
    ReduxActions.batchActions([
      actionCreators.order.resetOrder(payload),
      discountDisplacementSlice.actions.clear(undefined),
      discountDisplacementSlice.actions.clearCouponDisplacedPoints(undefined),
      discountDisplacementSlice.actions.clearPointsDisplacedCoupon(undefined),
    ]);
  }

  function addMissingOrderFields() {
    if (!profile) {
      return;
    }

    const replace = collect({
      customer_phone_number: profile.phone_number,
      customer_email: profile.email,
      customer_first_name: profile.first_name,
      customer_last_name: profile.last_name,
    }).filter((value, key) => !cached_order[key]).items;

    ReduxActions.dispatch(actionCreators.order.updateOrder(replace));
  }

  function isReduxOrderValid(): boolean {
    return !!cached_order?.method;
  }

  function toggleDuplicateOrderModal(order: Order = null): void {
    setDuplicateOrderModalProps({
      open: !!order,
      order,
    });
  }

  function getOrderMethodName(order: Partial<Order>): string {
    if (order.method === ORDER_METHODS.ROOM_SERVICE) {
      return t(
        `order_methods.${order.method}.${order.room_service_location?.type ?? RoomServiceLocationType.TableService}`,
      );
    }

    return t(`order_methods.${order.method}`);
  }

  const update: UpdateDataFunction = (field: string | Partial<OrderData>, value?: any) => {
    if (typeof field === 'object') {
      return ReduxActions.dispatch(actionCreators.order.updateOrder(field));
    }

    const parts = field.split('.');
    const key = parts[0];

    if (parts.length === 1) {
      return ReduxActions.dispatch(
        actionCreators.order.updateOrder({
          [key]: value,
        }),
      );
    }

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        [key]: {
          ...cached_order[key],
          [parts[1]]: value,
        },
      }),
    );
  };

  const context = useMemo(
    () => ({
      data: draft,
      localData: cached_order,
      ongoingOrder,
      loading,
      isOrderMethodInfoComplete,
      setOrderMethodInfoInfoComplete,
      isExternalDelivery,
      isCustomerInfoComplete,
      draftFailed,
      draftFailureReason,
      isDraftSettling,
      isDraftCurrent,
      retryDraft,
      validationErrors,
      getOrderData,
      draftOrder,
      setDraft: adoptDraft,
      setDraftUuid,
      getRetryOrderUuid,
      setRetryOrderUuid,
      createOrder,
      checkOrderNotDuplicate,
      refreshOngoingOrder,
      getOrderMethodName,
      addOngoingOrder: (data: Order) => ReduxActions.dispatch(actionCreators.order.addOngoingOrder(data)),
      removeOngoingOrder: (id: number) => ReduxActions.dispatch(actionCreators.order.removeOngoingOrder(id)),
      setValidationErrors,
      resetValidationErrors: () => setValidationErrors(null),
      update,
      resetOrder,
      setIgnorePaymentMethod,
    }),
    [
      draft,
      loading,
      cached_order,
      ongoingOrder,
      isOrderMethodInfoComplete,
      isCustomerInfoComplete,
      draftFailed,
      draftFailureReason,
      isDraftSettling,
      isDraftCurrent,
      retryDraft,
      validationErrors,
      draftOrder,
      getOrderData,
    ],
  );

  const renderDuplicateOrderModal = useMemo(() => {
    return (
      <DuplicateOrderModal
        open={duplicateOrderModalProps.open}
        order={duplicateOrderModalProps.order}
        onConfirm={() => (duplicateOrderAwareRef.current = true)}
        toggleModal={toggleDuplicateOrderModal}
      />
    );
  }, [duplicateOrderModalProps]);

  return (
    <OrderFlowContext.Provider value={context}>
      {children}
      {renderDuplicateOrderModal}
    </OrderFlowContext.Provider>
  );
}
